"use client";

import { useIsMobile } from "@/utils/useIsMobile";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";

interface Section {
  heading: string;
  content: string;
}

interface SeoAccordionProps {
  htmlContent: string;
}

export default function SeoSection({ htmlContent }: SeoAccordionProps) {
  const [sections, setSections] = useState<Section[]>([]);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const isMob = useIsMobile();

  useEffect(() => {
    if (!htmlContent) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const body = doc.body;
    const extracted: Section[] = [];

    let currentHeading = "";
    let currentContent = "";

    Array.from(body.childNodes).forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === "H2") {
        if (currentHeading) {
          extracted.push({ heading: currentHeading, content: currentContent });
          currentContent = "";
        }
        currentHeading = (node as HTMLElement).innerText;
      } else {
        if (currentHeading) {
          currentContent += (node as HTMLElement).outerHTML || node.textContent || "";
        }
      }
    });

    if (currentHeading) {
      extracted.push({ heading: currentHeading, content: currentContent });
    }

    setSections(extracted);
  }, [htmlContent]);

  const handleAccordion = (idx: number) => {
    if (!isMob) return; // only toggle on mobile
    setActiveIndex(activeIndex === idx ? null : idx);
  };

  return (
    <>
      {sections.map((sec, idx) => {
        let isActive = activeIndex === idx;

        if (!isMob) isActive = false;
        return (
          <div key={idx} className="seo-acc-section">
            <div className="seo-acc-head cursor-pointer sm:cursor-default" onClick={() => handleAccordion(idx)}>
              <h2 className="heading-text">{sec.heading}</h2>
              {isMob && (
                <svg width="14" height="14" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="7.56 8.49 13 8.73" className={`transition-all duration-350 ease-in-out ${isActive ? "text-primaryGreen rotate-180" : "text-primaryBlack"}`}>
                  <path d="M8.85742 9.78588L14.0574 15.9145L19.2574 9.78588" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" stroke="currentColor" />
                </svg>
              )}
            </div>

            {isMob ? (
              <AnimatePresence>
                {isActive && (
                  <motion.div className="seo-acc-content prose" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35, ease: "easeInOut" }}>
                    <div dangerouslySetInnerHTML={{ __html: sec.content }} />
                  </motion.div>
                )}
              </AnimatePresence>
            ) : (
              <div className="seo-acc-content prose" dangerouslySetInnerHTML={{ __html: sec.content }} />
            )}
          </div>
        );
      })}
    </>
  );
}
